import React, { useMemo } from "react";
import { ReusableDialog } from "../../../../../components/popupModals/ReusableDialog";
import { useDocumentRevisionInfo } from "../../../../../redux/hooks/useDocumentRevisionInfo";
import { useCheckinExistingFlow } from "../../../../../redux/hooks/useCheckinExistingFlow";
import { FILE_STATE } from "../../../../../redux/constants/metaData";
import { WorkingDocumentDetailsDialogTitle } from "./WorkingDocumentDetailsDialogTitle";
import { WorkingDocumentDetailsDialogContent } from "./WorkingDocumentDetailsDialogContent";
import { WorkingDocumentDetailsDialogActions } from "./WorkingDocumentDetailsDialogActions";

function computeViewRequiredFromDocClass(docClassFormatList, docClass) {
  if (!docClassFormatList || !Array.isArray(docClassFormatList) || !docClass) {
    return false;
  }
  for (const item of docClassFormatList) {
    if (item.code === docClass && item.viewRequired === "Y") return true;
    if (item.children && Array.isArray(item.children)) {
      for (const child of item.children) {
        if (child.code === docClass && child.viewRequired === "Y") return true;
      }
    }
  }
  return false;
}

export function WorkingDocumentDetailsDialog({
  isOpen,
  onClose,
  isCheckinMode = false,
  theme,
  getDocIcon,
  formatLastAction,
  deleteChkBox,
  setDeleteChkBox,
  isOneDriveDocDeleteEnable,
  selectedDocument,
  docReleaseStatus,
  onReleaseChange,
  viewCopyFiles,
  viewRequired,
  uploadViewCopyCount,
  createViewCopy,
  onCreateViewCopySelect,
  onViewCopyUploadChange,
  onSetDocumentToDeleteOneDrive,
  onCheckIn,
  onCheckinProcessStarted,
  getCheckinButtonText,
  docClassFormatList,
}) {
  const { runCheckinExisting, clear: clearCheckinExistingFlow } =
    useCheckinExistingFlow();
  const {
    documentRevisionData,
    isViewCopyExists: ifsReportsViewCopy,
    loading: isDetailsLoading,
    message: revisionMessage,
    isComplete: isDetailsComplete,
    isNoData: isDetailsNoData,
    isError: isDetailsError,
  } = useDocumentRevisionInfo(selectedDocument, isOpen, isCheckinMode);

  const workingIfsDocument = documentRevisionData ?? {};

  const isViewCopyMandatory = useMemo(() => {
    const fromList = computeViewRequiredFromDocClass(
      docClassFormatList,
      workingIfsDocument?.docClass,
    );
    return fromList || viewRequired === true;
  }, [docClassFormatList, workingIfsDocument?.docClass, viewRequired]);

  const hasViewCopySatisfied =
    (uploadViewCopyCount ?? 0) > 0 || createViewCopy === true;

  const disableCheckinForMissingViewCopy =
    isDetailsComplete &&
    workingIfsDocument?.FileState === FILE_STATE.checkedOut &&
    isViewCopyMandatory === true &&
    !hasViewCopySatisfied;

  const handleCheckInClick = (fileStateOrWorkingIfsDocument) => {
    const fileState =
      fileStateOrWorkingIfsDocument &&
      typeof fileStateOrWorkingIfsDocument === "object"
        ? fileStateOrWorkingIfsDocument?.FileState
        : fileStateOrWorkingIfsDocument;

    if (fileState === FILE_STATE.checkedOut) {
      runCheckinExisting({
        documentRevision: workingIfsDocument,
        oneDriveDocument: selectedDocument ?? {},
        createViewCopy: createViewCopy ?? false,
        viewCopyFiles: Array.isArray(viewCopyFiles) ? viewCopyFiles : [],
        docReleaseStatus: docReleaseStatus === false || docReleaseStatus == null ? "" : docReleaseStatus,
        objectConnections: [],
        deleteAfterSuccess: isOneDriveDocDeleteEnable === true || deleteChkBox === true,
      }).catch(() => {
        // Rejection is handled by checkinExistingFlow slice (message shown in process dialog)
        // Avoid unhandled rejection so dev overlay does not show [object Object]
      });
      onCheckinProcessStarted?.();
    } else {
      onCheckIn?.(fileStateOrWorkingIfsDocument);
    }
  };

  const contentText = revisionMessage ?? (isDetailsLoading ? "" : "");

  const handleClose = () => {
    clearCheckinExistingFlow();
    onClose();
  };

  return (
    <ReusableDialog
      isOpen={isOpen}
      onClose={handleClose}
      showHeader={false}
      variant="info"
      dialogMinHeight={isDetailsComplete ? 320 : 140}
      dialogMinWidth={isDetailsComplete ? 380 : 320}
      dialogMaxWidth={isDetailsComplete ? "min(92vw, 520px)" : "min(90vw, 420px)"}
      bodyMinHeight={isDetailsComplete ? 140 : 30}
      dialogMaxHeight="95vh"
      bodyMaxHeight={isDetailsComplete ? "65vh" : undefined}
      titleContent={
        <WorkingDocumentDetailsDialogTitle
          isDetailsComplete={isDetailsComplete}
          theme={theme}
          workingIfsDocument={workingIfsDocument}
          getDocIcon={getDocIcon}
        />
      }
      actions={
        <WorkingDocumentDetailsDialogActions
          isDetailsComplete={isDetailsComplete}
          isCheckinMode={isCheckinMode}
          theme={theme}
          onClose={handleClose}
          onCheckIn={handleCheckInClick}
          workingIfsDocument={workingIfsDocument}
          getCheckinButtonText={getCheckinButtonText}
          disableCheckin={disableCheckinForMissingViewCopy}
        />
      }
    >
      <WorkingDocumentDetailsDialogContent
        isDetailsComplete={isDetailsComplete}
        isDetailsLoading={isDetailsLoading}
        isDetailsError={isDetailsError}
        isDetailsNoData={isDetailsNoData}
        isCheckinMode={isCheckinMode}
        contentText={contentText}
        theme={theme}
        workingIfsDocument={workingIfsDocument}
        formatLastAction={formatLastAction}
        selectedDocument={selectedDocument}
        deleteChkBox={deleteChkBox}
        setDeleteChkBox={setDeleteChkBox}
        isOneDriveDocDeleteEnable={isOneDriveDocDeleteEnable}
        docReleaseStatus={docReleaseStatus}
        onReleaseChange={onReleaseChange}
        viewCopyFiles={viewCopyFiles}
        viewRequired={viewRequired}
        uploadViewCopyCount={uploadViewCopyCount}
        isViewCopyExists={ifsReportsViewCopy}
        onCreateViewCopySelect={onCreateViewCopySelect}
        onViewCopyUploadChange={onViewCopyUploadChange}
        onSetDocumentToDeleteOneDrive={onSetDocumentToDeleteOneDrive}
      />
    </ReusableDialog>
  );
}
